// src/views/producer/reports/price-distribution/parts/DeviationsFromRecommendedPriceInTime.vue
<template>
    <Card
        :header="$t('deviationsFromRecommendedPriceInTime')"
        :loading="request.getLoading()"
    >
        <template #nav>
            <div :key="spaceId" class="d-flex gap-3">
                <DropdownFilter v-model="currentNumberShown" :items="[10, 20, 50, null]" :prefix="`${$t('show')}: `" :disabled="request.getLoading()" />
                <DropdownFilter v-model="currentSortBy" :items="['summary', 'percentDiff']" :prefix="`${$t('sortBy')}: `" :disabled="request.getLoading()" />
                <DropdownFilter v-model="currentGroupBy" :items="['day', 'month', 'quarter', 'year']" :prefix="`${$t('range')}: `" :disabled="request.getLoading()" />
            </div>
        </template>

        <div class="d-flex flex-column gap-4 visibility-content-wrapper">
            <div
                v-for="item in visibleConfigs"
                :key="item.productKey"
                class="position-relative visibility-content-item"
            >
                <!-- przycisk edycji pokazujemy dopiero gdy nie jest placeholder -->
                <ButtonIcon
                    v-if="item.product && !item.isPlaceholder"
                    class="edit-btn wide"
                    size="sm"
                    :disabled="requestUpdatePrice.getLoading(item.product.id)"
                    icon="edit-2"
                    @click="openEditModal(item.product)"
                >
                    {{ item.product.price != null ? $format.currency(item.product.price) : $t('giveThePrice') }}
                </ButtonIcon>

                <!-- SKELETON placeholder 400 px (Bootstrap placeholder) -->
                <b-skeleton-img v-if="item.isPlaceholder" class="rounded" no-aspect height="400px" />

                <!-- Właściwy wykres (ten sam rozmiar) -->
                <EChart
                    v-else
                    class="chart-canvas"
                    :options="item.chartOptions"
                    :group-by="currentGroupBy"
                    type="time"
                    height="400px"
                />
            </div>
        </div>
    </Card>
</template>



<script>
    // lib path comment zgodnie z Twoją konwencją jest nad plikiem
    import EChart from '@/components/common/EChart.vue';
    import DropdownFilter from '@/components/common/DropdownFilter.vue';
    import { startOfDay, startOfMonth, startOfQuarter, startOfYear } from 'date-fns';

    export default {
        name: 'DeviationsFromRecommendedPriceInTime',
        components: { EChart, DropdownFilter },
        inject: {
            parentComponent: { default: null },
        },
        data() {
            return {
                worker: null,
                pendingWorkerJobs: new Map(),

                request: this.parentComponent.requestData,
                requestUpdatePrice: this.parentComponent.requestUpdatePrice,

                // Sterowanie UI
                currentNumberShown: 10, // 10 | 20 | 50 | null (wszystko)
                currentSortBy: 'summary', // 'summary' (PLN) | 'percentDiff' (%)
                currentGroupBy: 'day', // 'day' | 'month' | 'quarter' | 'year'

                // Limity/decymacja
                maxPointsPerSeries: 1500,
                hardMaxCharts: 80, // bezpieczny pułap dla "wszystkie" (null)

                // Render krokami (chunking)
                visibleConfigs: [], // [{ productKey, product, isPlaceholder?, chartOptions? }, ...]
                renderToken: 0,
                computeChunkSize: 6,

                /** Cache (LRU) */
                baseSeriesCache: new Map(), // Map<baseKey, basePrepared>
                chartOptionsCache: new Map(), // Map<baseKey|sortBy, frozenChartOptions>
                bucketsCache: new Map(), // Map<bucketsKey, timestamps[]>
                baseSeriesCacheMax: 200,
                chartOptionsCacheMax: 200,
                bucketsCacheMax: 50,
            };
        },

        computed: {
            loading() {
                return this.request.getLoading();
            },
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },

            rawProductsData() {
                return this.parentComponent.data?.products || {};
            },
            rawProductsKeysSignature() {
                const keys = Object.keys(this.rawProductsData).sort();
                return keys.join('|');
            },

            productsArray() {
                return Object.values(this.rawProductsData);
            },

            // Limit zanim zaczniemy obliczenia
            limitedProducts() {
                if (Number.isFinite(this.currentNumberShown)) {
                    return this.productsArray.slice(0, this.currentNumberShown);
                }
                return this.productsArray.slice(0, Math.min(this.productsArray.length, this.hardMaxCharts));
            },
        },

        watch: {
            currentNumberShown() {
                this.rebuildVisibleConfigsInChunks();
            },
            currentGroupBy() {
                this.rebuildVisibleConfigsInChunks();
            },
            currentSortBy() {
                this.rebuildVisibleConfigsInChunks();
            },
            rawProductsKeysSignature() {
                this.rebuildVisibleConfigsInChunks();
            },
            loading: {
                immediate: true,
                handler(isLoading) {
                    if (isLoading === false) {
                        this.clearAllCaches();
                        this.rebuildVisibleConfigsInChunks();
                    }
                },
            },
        },

        methods: {
            /** ---------------- Worker: import/init + RPC ---------------- */
            importWebWorker() {
                if (this.worker) return;

                this.worker = new Worker(new URL('./deviationsWorker.js', import.meta.url), { type: 'module' });

                this.worker.onmessage = event => {
                    const data = event.data || {};
                    if (!data.type) return;

                    // prosty ping/pong
                    if (data.type === 'pong') {
                        this.onWorkerMsg(event);
                        return;
                    }

                    // obsługa odpowiedzi na żądania z requestId
                    const job = data.requestId ? this.pendingWorkerJobs.get(data.requestId) : null;
                    if (!job) return;
                    this.pendingWorkerJobs.delete(data.requestId);
                    if (data.type === 'computeSlice:ok') {
                        job.resolve(data.payload);
                    } else if (data.type === 'computeSlice:error') {
                        job.reject(new Error(data.message || 'Worker error'));
                    }
                };
            },
            onWorkerMsg(e) {
                // debug/telemetria
                // console.log('Worker:', e.data);
            },
            computeSliceInWorker(preparedSlice, allBucketTimestamps) {
                this.importWebWorker();
                const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
                const message = {
                    type: 'computeSlice',
                    payload: {
                        requestId,
                        groupBy: this.currentGroupBy,
                        allBucketTimestamps,
                        products: preparedSlice,
                    },
                };
                return new Promise((resolve, reject) => {
                    this.pendingWorkerJobs.set(requestId, { resolve, reject });
                    this.worker.postMessage(message);
                });
            },
            /** Umożliwia przeglądarce odmalowanie między porcjami */
            yieldToMain() {
                return new Promise(resolve => {
                    if (typeof requestIdleCallback === 'function') {
                        requestIdleCallback(() => resolve());
                    } else {
                        requestAnimationFrame(() => resolve());
                    }
                });
            },

            /** ------------------------- LRU helpers (prawdziwe LRU) ------------------------- */
            getWithLRU(mapInstance, key) {
                if (!mapInstance.has(key)) return undefined;
                const value = mapInstance.get(key);
                mapInstance.delete(key);
                mapInstance.set(key, value);
                return value;
            },
            setWithLRU(mapInstance, key, value, maxSize) {
                if (mapInstance.has(key)) {
                    mapInstance.delete(key);
                } else if (mapInstance.size >= maxSize) {
                    const oldestKey = mapInstance.keys().next().value;
                    mapInstance.delete(oldestKey);
                }
                mapInstance.set(key, value);
            },

            /** ------------------------- Główny planista: placeholdery + porcjowanie ------------------------- */
            async rebuildVisibleConfigsInChunks() {
                this.renderToken += 1;
                const token = this.renderToken;

                const groupBy = this.currentGroupBy;

                // 1) Przygotuj minimalne dane + globalny min/max
                let globalMinTs = Number.POSITIVE_INFINITY;
                let globalMaxTs = Number.NEGATIVE_INFINITY;

                const preparedProducts = this.limitedProducts.map(({ product, price: history }, arrayIndex) => {
                    const productKey = product?.ean || product?.id || product?.name || `idx-${arrayIndex}`;
                    const records = Array.isArray(history) ? history : [];

                    const sortedHistory = this.maybeSortHistory(records)
                        .map(entry => {
                            const timestamp = new Date(entry.date).getTime();
                            const stores = {};
                            for (const [storeKey, rawValue] of Object.entries(entry)) {
                                if (storeKey === 'date') continue;
                                stores[storeKey] = rawValue;
                            }
                            return { date: entry.date, timestamp, stores };
                        })
                        .filter(item => Number.isFinite(item.timestamp));

                    if (sortedHistory.length) {
                        const minTs = sortedHistory[0].timestamp;
                        const maxTs = sortedHistory[sortedHistory.length - 1].timestamp;
                        if (minTs < globalMinTs) globalMinTs = minTs;
                        if (maxTs > globalMaxTs) globalMaxTs = maxTs;
                    }

                    return {
                        product,
                        productKey,
                        sortedHistory,
                        recommendedPrice: Number.isFinite(Number(product?.price)) ? Number(product.price) : null,
                    };
                });

                // 2) Placeholdery – bez skoków scrolla
                this.visibleConfigs = preparedProducts.map(pp => ({
                    productKey: pp.productKey,
                    product: pp.product,
                    isPlaceholder: true,
                }));
                await this.$nextTick();

                // 3) Brak danych – kończymy
                if (!Number.isFinite(globalMinTs) || !Number.isFinite(globalMaxTs)) {
                    return;
                }

                // 4) Globalne buckety + decymacja (reuse dla wszystkich produktów)
                const minAligned = this.getBucketStartTimestamp(globalMinTs, groupBy);
                const maxAligned = this.getBucketStartTimestamp(globalMaxTs, groupBy);
                const bucketsKey = `${minAligned}|${maxAligned}|${groupBy}`; // stabilny klucz
                const allBucketTimestamps = this.fromBucketsCacheOrBuild(bucketsKey, () => this.buildFullBuckets(minAligned, maxAligned, groupBy));
                const decimationIndices = this.buildDecimationIndices(allBucketTimestamps.length, this.maxPointsPerSeries);

                // 5) Przetwarzaj porcjami w workerze i renderuj
                for (let startIndex = 0; startIndex < preparedProducts.length; startIndex += this.computeChunkSize) {
                    if (token !== this.renderToken) return; // anulowane

                    const slice = preparedProducts.slice(startIndex, startIndex + this.computeChunkSize);

                    // 5a) Obliczenia w workerze – 1 żądanie per porcja
                    let workerResults;
                    try {
                        workerResults = await this.computeSliceInWorker(slice, allBucketTimestamps); // eslint-disable-line no-await-in-loop
                    } catch (error) {
                        // Fallback: lokalnie, bez zmiany logiki
                        workerResults = slice.map(pp => {
                            const basePrepared = this.buildBaseSeriesForProduct(pp, allBucketTimestamps);
                            return { productKey: pp.productKey, basePrepared };
                        });
                    }

                    // 5b) Złóż chartOptions, aktualizuj cache i podmień placeholdery
                    for (let localIdx = 0; localIdx < workerResults.length; localIdx += 1) {
                        const resultItem = workerResults[localIdx];
                        const targetIndex = startIndex + localIdx;

                        const pp = slice[localIdx];
                        const baseKey = this.makeBaseCacheKey(pp, bucketsKey);

                        // Jeśli worker zwrócił basePrepared – włóż do cache
                        if (resultItem && resultItem.basePrepared) {
                            this.setWithLRU(this.baseSeriesCache, baseKey, resultItem.basePrepared, this.baseSeriesCacheMax);
                        }

                        const cachedBasePrepared = this.getWithLRU(this.baseSeriesCache, baseKey);
                        const basePrepared = cachedBasePrepared || (resultItem ? resultItem.basePrepared : null) || this.buildBaseSeriesForProduct(pp, allBucketTimestamps);

                        // meta (worker nie zna pełnego obiektu product)
                        basePrepared.productMeta = {
                            name: pp.product?.name || '',
                            ean: pp.product?.ean || '',
                            productKey: pp.productKey,
                        };

                        const chartKey = `${baseKey}|${this.currentSortBy}|${this.$i18n?.locale || 'default'}`;
                        const chartOptions = this.fromChartOptionsCacheOrBuild(chartKey, () => this.buildChartOptionsFromBase(basePrepared, decimationIndices));

                        this.$set(this.visibleConfigs, targetIndex, {
                            productKey: pp.productKey,
                            product: pp.product,
                            chartOptions,
                            isPlaceholder: false,
                        });

                        // Mała pauza co 2 wykresy – redukcja LongTasków i lagów UI
                        if (localIdx % 2 === 1) {
                            // eslint-disable-next-line no-await-in-loop
                            await this.yieldToMain();
                        }
                    }
                }
            },

            /** ------------------------- UTIL: czyszczenie cache ------------------------- */
            clearAllCaches() {
                this.baseSeriesCache.clear();
                this.chartOptionsCache.clear();
                this.bucketsCache.clear();
            },

            /** ------------------------- FORMATERY ------------------------- */
            formatTooltipValue(value) {
                if (this.currentSortBy === 'percentDiff') return this.$format.percent(value);
                if (this.currentSortBy === 'summary') return this.$format.currency(value);
                return value;
            },
            formatAxisLabel(value) {
                if (this.currentSortBy === 'percentDiff') return this.$format.percent(value);
                if (this.currentSortBy === 'summary') return this.$format.currency(value, null, null, 0);
                return value;
            },

            /** ------------------------- BUCKETY / DATY ------------------------- */
            getBucketStartTimestamp(timestampMs, groupBy) {
                const originalDate = new Date(Number(timestampMs));
                switch (groupBy) {
                    case 'day':
                        return startOfDay(originalDate).getTime();
                    case 'month':
                        return startOfMonth(originalDate).getTime();
                    case 'quarter':
                        return startOfQuarter(originalDate).getTime();
                    case 'year':
                        return startOfYear(originalDate).getTime();
                    default:
                        return startOfDay(originalDate).getTime();
                }
            },
            buildFullBuckets(startAlignedTs, endAlignedTs, groupBy) {
                if (startAlignedTs == null || endAlignedTs == null) return [];
                const buckets = [];
                // inclusive end – ostatni bucket zawiera endAlignedTs
                const stepFn =
                    {
                        day: date => date.setDate(date.getDate() + 1),
                        month: date => date.setMonth(date.getMonth() + 1),
                        quarter: date => date.setMonth(date.getMonth() + 3),
                        year: date => date.setFullYear(date.getFullYear() + 1),
                    }[groupBy] || (date => date.setDate(date.getDate() + 1));

                const cursor = new Date(startAlignedTs);
                while (cursor.getTime() <= endAlignedTs) {
                    buckets.push(this.getBucketStartTimestamp(cursor.getTime(), groupBy));
                    stepFn(cursor);
                }
                return buckets;
            },

            /** ------------------------- DECYMACJA ------------------------- */
            buildDecimationIndices(length, maxPoints) {
                if (!maxPoints || length <= maxPoints) return null;
                const step = Math.ceil(length / maxPoints);
                const indices = [];
                for (let index = 0; index < length; index += step) indices.push(index);
                if (indices[indices.length - 1] !== length - 1) indices.push(length - 1);
                return indices;
            },
            applyDecimation(xyArray, indices) {
                if (!indices) return xyArray;
                const decimated = new Array(indices.length);
                for (let index = 0; index < indices.length; index++) {
                    decimated[index] = xyArray[indices[index]];
                }
                return decimated;
            },

            /** ------------------------- SORT / PREP ------------------------- */
            isSortedAscendingByDate(records) {
                let prevTs = -Infinity;
                for (let index = 0; index < records.length; index++) {
                    const ts = new Date(records[index].date).getTime();
                    if (!Number.isFinite(ts)) continue;
                    if (ts < prevTs) return false;
                    prevTs = ts;
                }
                return true;
            },
            maybeSortHistory(records) {
                if (this.isSortedAscendingByDate(records)) return records;
                return records.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
            },

            /** ------------------------- CACHE: BUCKETS ------------------------- */
            fromBucketsCacheOrBuild(cacheKey, builder) {
                const cached = this.getWithLRU(this.bucketsCache, cacheKey);
                if (cached !== undefined) return cached;
                const built = builder();
                this.setWithLRU(this.bucketsCache, cacheKey, built, this.bucketsCacheMax);
                return built;
            },

            /** ------------------------- CACHE: BASE SERIES ------------------------- */
            makeBaseCacheKey(preparedProduct, bucketsKey) {
                const groupBy = this.currentGroupBy;
                const recommendedPrice = Number.isFinite(preparedProduct.recommendedPrice) ? preparedProduct.recommendedPrice : 'null';

                const storeNameSet = new Set();
                for (const rec of preparedProduct.sortedHistory) {
                    for (const storeName of Object.keys(rec.stores)) storeNameSet.add(storeName);
                }
                const storeNamesSig = Array.from(storeNameSet).sort().join(',');

                return [preparedProduct.productKey, groupBy, recommendedPrice, storeNamesSig, bucketsKey].join('|');
            },
            fromBaseCacheOrBuild(baseKey, builder) {
                const cached = this.getWithLRU(this.baseSeriesCache, baseKey);
                if (cached !== undefined) return cached;
                const built = builder();
                this.setWithLRU(this.baseSeriesCache, baseKey, built, this.baseSeriesCacheMax);
                return built;
            },

            /** ------------------------- BUDOWA BAZY (fallback lokalny) ------------------------- */
            buildBaseSeriesForProduct(preparedProduct, allBucketTimestamps) {
                const { product, productKey, sortedHistory, recommendedPrice } = preparedProduct;

                const storeNames = this.collectStoreNames(sortedHistory);
                const storeBucketsMap = this.buildStoreBucketsMap(sortedHistory, this.currentGroupBy);

                const seriesByStore = {};
                for (const storeName of storeNames) {
                    const bucketMap = storeBucketsMap.get(storeName);
                    const xyAvg = new Array(allBucketTimestamps.length);
                    for (let index = 0; index < allBucketTimestamps.length; index++) {
                        const bucketTs = allBucketTimestamps[index];
                        const bucket = bucketMap?.get(bucketTs);
                        const avg = bucket ? bucket.sum / bucket.count : null;
                        xyAvg[index] = [bucketTs, avg];
                    }
                    seriesByStore[storeName] = { xyAvg, xyPercent: null };
                }

                const referenceLineAvg = recommendedPrice != null ? allBucketTimestamps.map(ts => [ts, recommendedPrice]) : [];
                const referenceLinePercent = allBucketTimestamps.map(ts => [ts, 0]);

                return {
                    productMeta: { name: product?.name || '', ean: product?.ean || '', productKey },
                    recommendedPrice,
                    storeNames,
                    seriesByStore,
                    referenceLineAvg,
                    referenceLinePercent,
                    isPercentSeriesComputed: false,
                };
            },

            /** ------------------------- AGREGACJA DANYCH ------------------------- */
            ensurePercentSeriesComputed(basePrepared) {
                if (basePrepared.isPercentSeriesComputed) return;
                const recPrice = basePrepared.recommendedPrice;
                for (const storeName of basePrepared.storeNames) {
                    const storeSeries = basePrepared.seriesByStore[storeName];
                    if (storeSeries.xyPercent) continue;
                    const xyPercent = new Array(storeSeries.xyAvg.length);
                    for (let index = 0; index < storeSeries.xyAvg.length; index++) {
                        const ts = storeSeries.xyAvg[index][0];
                        const avg = storeSeries.xyAvg[index][1];
                        const canCompute = Number.isFinite(recPrice) && recPrice !== 0;
                        xyPercent[index] = [ts, avg != null && canCompute ? ((avg - recPrice) / recPrice) * 100 : null];
                    }
                    storeSeries.xyPercent = xyPercent;
                }
                basePrepared.isPercentSeriesComputed = true; // eslint-disable-line no-param-reassign
            },
            collectStoreNames(sortedHistory) {
                const setAcc = new Set();
                for (const entry of sortedHistory) {
                    const stores = entry.stores || {};
                    for (const storeName of Object.keys(stores)) setAcc.add(storeName);
                }
                return Array.from(setAcc).sort();
            },
            buildStoreBucketsMap(sortedHistory, groupBy) {
                const storeBucketsMap = new Map();
                const localBucketCache = new Map(); // Map<number, number>

                const getBucket = ts => {
                    const hit = localBucketCache.get(ts);
                    if (hit != null) return hit;
                    const calc = this.getBucketStartTimestamp(ts, groupBy);
                    localBucketCache.set(ts, calc);
                    return calc;
                };

                for (const item of sortedHistory) {
                    const bucketStartTs = getBucket(item.timestamp);
                    const stores = item.stores || {};
                    for (const [storeName, rawValue] of Object.entries(stores)) {
                        const numericValue = Number(rawValue);
                        if (!Number.isFinite(numericValue)) continue;

                        let bucketsMapForStore = storeBucketsMap.get(storeName);
                        if (!bucketsMapForStore) {
                            bucketsMapForStore = new Map();
                            storeBucketsMap.set(storeName, bucketsMapForStore);
                        }
                        const bucket = bucketsMapForStore.get(bucketStartTs);
                        if (!bucket) {
                            bucketsMapForStore.set(bucketStartTs, { sum: numericValue, count: 1 });
                        } else {
                            bucket.sum += numericValue;
                            bucket.count += 1;
                        }
                    }
                }
                return storeBucketsMap;
            },

            /** ------------------------- CACHE: CHART OPTIONS ------------------------- */
            fromChartOptionsCacheOrBuild(cacheKey, builder) {
                const cached = this.getWithLRU(this.chartOptionsCache, cacheKey);
                if (cached !== undefined) return cached;
                const built = builder();
                const frozen = Object.freeze(built);
                this.setWithLRU(this.chartOptionsCache, cacheKey, frozen, this.chartOptionsCacheMax);
                return frozen;
            },

            /** ------------------------- BUDOWA OPCJI WYKRESU ------------------------- */
            buildChartOptionsFromBase(basePrepared, decimationIndices) {
                const isPercentMode = this.currentSortBy === 'percentDiff';
                if (isPercentMode) this.ensurePercentSeriesComputed(basePrepared);

                // lokalne, czyste formatery – nie trzymają referencji do instancji w cache
                const valueFormatter = value => (isPercentMode ? this.$format.percent(value) : this.$format.currency(value));
                const axisFormatter = value => (isPercentMode ? this.$format.percent(value) : this.$format.currency(value, null, null, 0));

                const hasRecommended = basePrepared.recommendedPrice != null;
                const referenceLineSeries =
                    isPercentMode || hasRecommended
                        ? {
                              name: this.$t('recommendedPrice'),
                              type: 'line',
                              smooth: false,
                              data: this.applyDecimation(isPercentMode ? basePrepared.referenceLinePercent : basePrepared.referenceLineAvg, decimationIndices),
                              lineStyle: { color: 'black' },
                              itemStyle: { color: 'black' },
                              symbol: 'none',
                              areaStyle: null,
                              connectNulls: true,
                              showSymbol: false,
                              sampling: 'lttb',
                              progressive: 5000,
                              progressiveThreshold: 10000,
                              progressiveChunkMode: 'mod',
                              animation: false,
                          }
                        : null;

                const series = referenceLineSeries ? [referenceLineSeries] : [];
                const legendData = referenceLineSeries ? [this.$t('recommendedPrice'), ...basePrepared.storeNames] : [...basePrepared.storeNames];

                const commonSeriesOpts = {
                    areaStyle: null,
                    connectNulls: false,
                    showSymbol: false,
                    sampling: 'lttb',
                    progressive: 5000,
                    progressiveThreshold: 10000,
                    progressiveChunkMode: 'mod',
                    animation: false,
                };

                const storeSeries = basePrepared.storeNames.map(storeName => {
                    const storeSeriesBase = basePrepared.seriesByStore[storeName];
                    const xyBase = isPercentMode ? storeSeriesBase.xyPercent : storeSeriesBase.xyAvg;
                    const xy = xyBase || (storeSeriesBase.xyAvg ? storeSeriesBase.xyAvg.map(([ts]) => [ts, null]) : []);
                    return {
                        name: storeName,
                        type: 'line',
                        data: this.applyDecimation(xy, decimationIndices),
                        ...commonSeriesOpts,
                    };
                });

                return {
                    grid: { top: '80px' },
                    title: {
                        text: basePrepared.productMeta.name,
                        subtext: `EAN: ${basePrepared.productMeta.ean}`,
                        textStyle: { fontSize: 14 },
                    },
                    // dla porównywania wielu serii w pionie
                    tooltip: { trigger: 'axis', valueFormatter },
                    xAxis: { boundaryGap: false },
                    yAxis: { type: 'value', axisLabel: { formatter: axisFormatter } },
                    series: [...series, ...storeSeries],
                    legend: { data: legendData },
                };
            },

            openEditModal(product) {
                this.$root.$emit('bv::show::modal', 'modal-edit-recommended-price');
                this.$root.$emit('modal-edit-recommended-price', product);
            },
        },
        created() {
            this.importWebWorker();
            this.worker.postMessage({ type: 'ping', payload: { ok: true } });
        },
        mounted() {
            this.rebuildVisibleConfigsInChunks();
        },
        beforeDestroy() {
            try {
                if (this.worker) {
                    this.worker.onmessage = null;
                    this.worker.terminate();
                }
            } catch (error) {
                // error
            }
            this.pendingWorkerJobs.forEach(job => job.reject?.(new Error('Component destroyed')));
            this.pendingWorkerJobs.clear();
            this.clearAllCaches();
        },
    };
</script>



<style lang="scss" scoped>
    .edit-btn {
        position: absolute;
        right: 10px;
        top: 10px;
        z-index: 10;
    }

    /* wrapper z content-visibility dla całej kolumny */
    .visibility-content-wrapper {
        content-visibility: auto;

        .visibility-content-item {
            contain-intrinsic-size: 1200px 400px;
        }
    }
</style>
